import { ReactNode, useId } from "react";
import { Check } from "lucide-react";

import styles from "./Checkbox.module.css";

interface Props {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  /**
   * Extra class on the outer wrapper — this is how a caller nudges the box
   * without the component baking layout in. The export mockup's include rows
   * (transfer-export.html L140) add `margin-top: 2px` so the 16px box lines up
   * with the first text line of a `align-items: start` grid cell.
   */
  className?: string;
}

/**
 * Checkbox — the `.cbx` of the transfer mockups
 * (.codelane/artifacts/t-27/transfer-export.html L140-L142,
 *  .codelane/artifacts/t-27/transfer-import-diff.html L129-L131).
 *
 * A real `<input type="checkbox">` does the work — role, `aria-checked`, Space
 * activation and the disabled semantics all come from the platform. It is
 * visually hidden but still focusable, and the painted `.box` sibling carries
 * the mockup's styling plus the focus ring.
 *
 * Props mirror Switch so the two read as one family. Naming: the visible
 * `label` normally names the box; pass `aria-label` when there is no visible
 * label, or when the visible text states a value rather than a name.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  id,
  "aria-label": ariaLabel,
  className,
}: Props) {
  const reactId = useId();
  const fid = id ?? reactId;

  const wrapClass = [
    styles.wrap,
    disabled ? styles.wrapDisabled : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const boxClass = [
    styles.box,
    checked ? styles.on : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={wrapClass} htmlFor={fid}>
      <input
        id={fid}
        type="checkbox"
        className={styles.input}
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={boxClass} aria-hidden="true">
        {checked && <Check size={10} strokeWidth={3.5} />}
      </span>
      {label && (
        <span
          className={[styles.label, disabled ? styles.labelDisabled : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {label}
        </span>
      )}
    </label>
  );
}
